<template>
  <v-text-field
      rounded="lg"
      :error-messages="error"
      :label="translation(translationList.vehicle_max_weight, 'Masa maximă autorizată')"
      v-model="maxWeightString"
      name="max_weight"
      :rules="maxWeightValidationRules"
      @input="onInput"
      variant="outlined"
      bg-color="surface"
      :tile="true"
      suffix="kg"
      type="number"
  >
    <template v-slot:append-inner>
      <v-tooltip location="top" open-delay="500" open-on-click open-on-hover>
        <template v-slot:activator="{ props }">
           <v-icon v-bind="props" class="cursor-pointer" aria-label="info">mdi-information</v-icon>
        </template>
        <span>
          <NuxtImg
              src="/images/vehicle/registration_template/template_f1.png"
              class="h-100"
              loading="lazy"
              alt="Registration template"
          />
        </span>
      </v-tooltip>
    </template>
  </v-text-field>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, watch, toRefs } from 'vue';
import { useTranslationStore, translation } from '~/plugins/store/translations';
import type {TranslationList} from "~/types/Utils";

export default defineComponent({
  props: {
    modelValue: Number as () => number | null,
  },
  setup(props, { emit }) {
    const { modelValue } = toRefs(props);
    const maxWeight = ref<number | null>(modelValue.value);
    const maxWeightString = ref<string | null>(modelValue.value !== null ? String(modelValue.value) : null);
    const error = ref<string[]>([]);

    watch(maxWeight, (newValue) => {
      const isValid = validateMaxWeight(newValue);
      if (isValid) {
        emit('update:modelValue', newValue);
      } else {
        emit('update:modelValue', null);
      }
      emit('validation-vehicle-status', { field: 'max_weight', isValid });
    });

    watch(maxWeightString, (newValue) => {
      const parsedValue = parseFloat(newValue as string);
      if (!isNaN(parsedValue)) {
        maxWeight.value = parsedValue;
      } else {
        maxWeight.value = null;
      }
    });

    function validateMaxWeight(value: number | null, showErrors?: boolean): boolean {
      error.value = [];
      if (value !== null) {
        if (showErrors) {
          const errors = maxWeightValidationRules
              .map(rule => rule(value))
              .filter(result => result !== true) as string[];
          if (errors.length > 0) {
            error.value = errors;
            return false;
          }
        }
        return maxWeightValidationRules.every(rule => rule(value) === true);
      } else if (showErrors) {
        error.value.push(translation(translationList.value.required, 'Acest camp este obligatoriu'));
        return false;
      }
      return false;
    }

    const translationList = ref<TranslationList>({});
    const translationStore = useTranslationStore();

    const maxWeightValidationRules = [
      (v: number) => !!v || translation(translationList.value.required, 'Acest camp este obligatoriu'),
      (v: number) => v >= 0 || translation(translationList.value.invalid_field, 'Acest camp este invalid'),
    ];

    const onInput = (event: Event) => {
      const input = event.target as HTMLInputElement;
      maxWeightString.value = input.value;
    };

    onMounted(async () => {
      translationList.value = await translationStore.getTranslations('ro', 'dashboard');

      // Listen for validate event
      window.addEventListener('validate-vehicle', (data) => {
        if (data.detail && data.detail === 'max_weight') {
          const isValid = validateMaxWeight(maxWeight.value, true);
          if (isValid) {
            emit('validation-vehicle-status', { field: 'max_weight', isValid });
          }
        }
      });
    });

    watch(modelValue, (newValue) => {
      const formattedValue = newValue !== null ? String(newValue) : null;
      if (formattedValue !== maxWeightString.value) {
        maxWeightString.value = formattedValue;
      }
    });

    return {
      maxWeight,
      maxWeightString,
      maxWeightValidationRules,
      translationList,
      translation,
      onInput,
      error,
    };
  },
});
</script>
